import React from 'react';
import { StockDataPoint } from '../types/StockData';

interface SignalsTabProps {
  selectedDataPoint: StockDataPoint | null;
}

const SignalsTab: React.FC<SignalsTabProps> = ({ selectedDataPoint }) => {
  const hasSignals = selectedDataPoint?.signals && selectedDataPoint.signals.length > 0;
  const hasKeyAlerts = selectedDataPoint?.keyAlerts && selectedDataPoint.keyAlerts.length > 0;

  if (!hasSignals && !hasKeyAlerts) {
    return (
      <div className="text-center py-4 text-gray-500">
        No signals or key events available for this time window
      </div>
    );
  }

  const signals = selectedDataPoint.signals || [];
  const keyAlerts = selectedDataPoint.keyAlerts || [];

  // Sort signals by priority (1 first, then 2, etc.)
  const sortedSignals = [...signals].sort((a, b) => (a.priority || 999) - (b.priority || 999));

  return (
    <div className="space-y-4">
      {/* Key Events Section */}
      {keyAlerts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-orange-500 text-lg">🔔</span>
            <h3 className="text-sm font-semibold text-gray-700">
              Key Events ({keyAlerts.length})
            </h3>
          </div>
          <div className="space-y-2">
            {keyAlerts.map((alert, index) => (
              <div key={`key-alert-${index}`} className="bg-orange-50 border-l-4 border-orange-400 p-3 rounded">
                <div className="flex items-center gap-2">
                  <span className="text-orange-500">🔔</span>
                  <span className="text-sm text-orange-800 font-medium">{alert}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trading Signals Section */}
      {sortedSignals.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-blue-500 text-lg">📈</span>
            <h3 className="text-sm font-semibold text-gray-700">
              Trading Signals ({sortedSignals.length})
            </h3>
          </div>
          <div className="space-y-2">
            {sortedSignals.map((signal, index) => (
              <div key={`signal-${index}`} className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-semibold text-blue-800">{signal.signalType}</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded ${
                      signal.urgency === 'HIGH' ? 'bg-red-100 text-red-800' :
                      signal.urgency === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {signal.urgency}
                    </span>
                    {signal.category && (
                      <span className="ml-2 px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">
                        {signal.category}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-600">Confidence: {signal.confidence}%</span>
                    {signal.priority && (
                      <div className="text-xs text-gray-500 mt-1">Priority: {signal.priority}</div>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-700 mt-1">{signal.description}</p>
                <span className={`inline-block mt-1 px-2 py-1 text-xs rounded ${
                  signal.action === 'BUY' ? 'bg-green-100 text-green-800' :
                  signal.action === 'SELL' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {signal.action}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SignalsTab;
